import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Users, FolderPlus, UserCircle, Calendar, Target, Menu, X, LogOut, Search, MapPin, Share2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import SearchBar from './SearchBar';
import PulseLogo from '../assets/PulseLogoMin.png'; // Ajuste le chemin selon où tu places ton image

function Navbar() {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [isPWA, setIsPWA] = useState(false);

  // Détecter si l'app est en mode PWA
  React.useEffect(() => {
    const checkPWA = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches
        || window.navigator.standalone
        || document.referrer.includes('android-app://');
      setIsPWA(isStandalone);
    };
    checkPWA();
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setIsMobileSearchOpen(false);
  };

  const toggleMobileSearch = () => {
    setIsMobileSearchOpen(!isMobileSearchOpen);
    setIsMobileMenuOpen(false);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    closeMobileMenu();
    navigate('/');
  };

  const handleShare = async () => {
  if (navigator.share) {
    try {
      await navigator.share({
        title: 'Run Clubs',
        text: 'Rejoins-moi sur Run Clubs - Trouve et rejoins des clubs de course près de chez toi !',
        url: 'https://runclubs.vercel.app/'
      });
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Erreur lors du partage:', error);
      }
    }
  } else {
    // Fallback: copier dans le presse-papier
    navigator.clipboard.writeText('https://runclubs.vercel.app/');
    alert('Lien copié dans le presse-papier !');
  }
};

  return (
    <>
      <nav className="bg-slate-900 border-b border-slate-800 sticky top-0 z-50 shadow-xl">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-4">
            {/* Logo et titre */}
            <Link to="/" className="flex items-center space-x-2 flex-shrink-0 group" onClick={closeMobileMenu}>
              <img 
                src={PulseLogo} 
                alt="Run Clubs Logo" 
                className="w-10 h-10 object-contain transition-transform group-hover:scale-110"
              />
              <h1 className="text-2xl font-bold text-white group-hover:text-indigo-500 transition">Pulse</h1>
            </Link>

            {/* Barre de recherche - cachée sur mobile */}
            <div className="hidden md:flex flex-1 max-w-2xl mx-4">
              <SearchBar />
            </div>

            {/* Icônes pour mobile */}
            <div className="flex md:hidden items-center space-x-3">
              {/* Masquer la recherche en mode PWA (elle est dans le footer) */}
              {!isPWA && (
                <button
                  onClick={toggleMobileSearch}
                  className="text-gray-400 hover:text-indigo-500 transition"
                  aria-label="Recherche"
                >
                  <Search size={24} />
                </button>
              )}
              <button
                onClick={toggleMobileMenu}
                className="text-gray-400 hover:text-indigo-500 transition"
                aria-label="Menu"
              >
                {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
              </button>
            </div>
            
            {/* Navigation desktop */}
            <div className="hidden md:flex items-center space-x-3 flex-shrink-0">
              {isAuthenticated ? (
                <>
                  <Link
                    to="/calendar"
                    className="flex items-center space-x-1 text-gray-300 hover:text-indigo-500 font-semibold px-3 py-2 rounded-lg hover:bg-slate-800 transition"
                    title="Mon calendrier"
                  >
                    <Calendar size={22} />
                    <span className="hidden xl:inline">Calendrier</span>
                  </Link>

                  <Link
                    to="/profile"
                    className="flex items-center space-x-2 text-gray-300 hover:text-indigo-500 px-3 py-2 rounded-lg hover:bg-slate-800 transition"
                    title="Mon profil"
                  >
                    <UserCircle size={26} />
                    <span className="font-semibold hidden 2xl:inline">
                      {user.prenom} {user.nom}
                      {user.role === 'admin' && (
                        <span className="ml-2 text-xs bg-indigo-500 text-white px-2 py-1 rounded">ADMIN</span>
                      )}
                    </span>
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="text-gray-300 hover:text-white font-semibold px-4 py-2 rounded-lg hover:bg-slate-800 transition"
                  >
                    Connexion
                  </Link>
                  <Link
                    to="/register"
                    className="bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-lg transition font-semibold shadow-lg shadow-indigo-500/20"
                  >
                    S'inscrire
                  </Link>
                </>
              )}
            </div>
          </div>

          {/* Barre de recherche mobile */}
          {isMobileSearchOpen && (
            <div className="md:hidden mt-3 pb-2">
              <SearchBar />
            </div>
          )}
        </div>
      </nav>

      {/* Menu mobile - Drawer latéral */}
      {isMobileMenuOpen && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black bg-opacity-70 z-40 md:hidden backdrop-blur-sm"
            onClick={closeMobileMenu}
          ></div>

          {/* Drawer */}
          <div className="fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-slate-900 border-l border-slate-800 shadow-2xl z-50 md:hidden overflow-y-auto">
            <div className="p-6">
              {/* Header du drawer */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Menu</h2>
                <button
                  onClick={closeMobileMenu}
                  className="text-gray-400 hover:text-white transition"
                  aria-label="Fermer le menu"
                >
                  <X size={28} />
                </button>
              </div>

              {isAuthenticated ? (
                <>
                  {/* Profil utilisateur - Masqué en mode PWA (il est dans le footer) */}
                  {!isPWA && (
                    <Link
                      to="/profile"
                      onClick={closeMobileMenu}
                      className="flex items-center space-x-3 p-4 mb-4 bg-slate-800 border border-slate-700 rounded-lg hover:border-indigo-500 transition"
                    >
                      <UserCircle size={40} className="text-indigo-500" />
                      <div>
                        <p className="font-semibold text-white">
                          {user.prenom} {user.nom}
                        </p>
                        {user.role === 'admin' && (
                          <span className="text-xs bg-indigo-500 text-white px-2 py-1 rounded mt-1 inline-block">
                            ADMIN
                          </span>
                        )}
                      </div>
                    </Link>
                  )}

                  {/* Navigation */}
                  <nav className="space-y-2">
                    {/* Calendrier - Masqué en mode PWA (il est dans le footer) */}
                    {!isPWA && (
                      <Link
                        to="/calendar"
                        onClick={closeMobileMenu}
                        className="flex items-center space-x-3 p-4 rounded-lg hover:bg-slate-800 border border-transparent hover:border-slate-700 transition"
                      >
                        <Calendar size={24} className="text-indigo-500" />
                        <span className="font-medium text-gray-300">Mon calendrier</span>
                      </Link>
                    )}
                    {/* 🆕 BOUTON PARTAGE */}
  <button
  onClick={handleShare}
  className="flex items-center space-x-3 p-4 rounded-lg hover:bg-slate-800 border border-transparent hover:border-slate-700 transition w-full text-left"
>
  <Share2 size={24} className="text-indigo-500" />
  <span className="font-medium text-gray-300">Partager l'app</span>
</button>
                    <div className="border-t border-slate-800 my-4"></div>

                    <button
                      onClick={handleLogout}
                      className="flex items-center space-x-3 p-4 rounded-lg hover:bg-red-900/20 border border-transparent hover:border-red-800 transition w-full text-left"
                    >
                      <LogOut size={24} className="text-red-500" />
                      <span className="font-medium text-red-500">Déconnexion</span>
                    </button>
                  </nav>
                </>
              ) : (
                <div className="space-y-3">
                  <Link
                    to="/login"
                    onClick={closeMobileMenu}
                    className="block w-full text-center text-gray-300 hover:text-white font-semibold py-3 px-4 rounded-lg border-2 border-slate-700 hover:border-indigo-500 hover:bg-slate-800 transition"
                  >
                    Connexion
                  </Link>
                  <Link
                    to="/register"
                    onClick={closeMobileMenu}
                    className="block w-full text-center bg-indigo-500 hover:bg-indigo-600 text-white py-3 px-4 rounded-lg transition font-semibold shadow-lg shadow-indigo-500/20"
                  >
                    S'inscrire
                  </Link>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default Navbar;